import { useQuery } from '@tanstack/react-query';
import { awaitJSON, LinearBuyingLambdaFetch, S3SignedUrlFetch } from '@src/utils/fetch-utils';

export interface RawSpot {
  stationName: string;
  avail: string;
  creative: string;
  spotLength: number;
  easternSpotTime: number;
  sellingRotation: string;
  netRate: number;
  program: string;
}

// Keyed by the Monday of each week. A competitor with no spots file yields null.
export type CompetitorSpotsRawData = Record<string, RawSpot[]>;

export const useCompetitorSpots = (selectedCompetitor: string | undefined) =>
  useQuery({
    queryKey: ['competitiveSpots', selectedCompetitor],
    enabled: Boolean(selectedCompetitor),
    // Missing competitor data is common; the page raises its own error dialog without reporting it.
    meta: { suppressGlobalError: true },
    retry: false,
    queryFn: async () => {
      const res = await S3SignedUrlFetch(
        `bpm-ml-data/competitive/latest/${selectedCompetitor}_spots.json.gz`
      );
      // Not every configured competitor has a spots file; S3 answers 403/404 (NoSuchKey) for those.
      if (res.status === 403 || res.status === 404) {
        return null;
      }
      if (!res.ok) {
        throw new Error(`Spots request failed (${res.status})`);
      }
      return awaitJSON<CompetitorSpotsRawData>(res);
    }
  });

export const useStationNames = () =>
  useQuery({
    queryKey: ['linearBuyingStationNames'],
    meta: { suppressGlobalError: true },
    queryFn: async () => {
      const res = await LinearBuyingLambdaFetch('/networks');
      const networks = await awaitJSON<Record<string, { stationName: string }>>(res);
      return Object.fromEntries(
        Object.entries(networks).map(([network, { stationName }]) => [network, stationName])
      );
    }
  });
